// src/utils/patternInsights.js
// Surfaces hidden patterns in trade data
// Pure statistical analysis — no AI required
// Runs on every user's own trade history

export function generatePatternInsights(trades, settings) {
  const closed = (trades || []).filter(t => t.status !== 'open' && t.date);
  if (closed.length < 10) return { patterns: [], warnings: [], discoveries: [] };

  const startBal = settings?.startingBalance || 0;
  const patterns    = [];
  const warnings    = [];
  const discoveries = [];

  // ── 1. PAIR PERFORMANCE ───────────────────────────────────────
  const byPair = groupBy(closed, 'pair');
  const pairStats = Object.entries(byPair)
    .filter(([, ts]) => ts.length >= 5)
    .map(([pair, ts]) => ({
      pair,
      count:   ts.length,
      wins:    ts.filter(t => t.result === 'Win').length,
      wr:      Math.round((ts.filter(t => t.result === 'Win').length / ts.length) * 100),
      pnl:     ts.reduce((s, t) => s + Number(t.pnl || 0), 0),
      avgRR:   avg(ts.filter(t => t.rrAchieved).map(t => Number(t.rrAchieved))),
    }))
    .sort((a, b) => b.wr - a.wr);

  const bestPair  = pairStats[0];
  const worstPair = pairStats[pairStats.length - 1];

  if (bestPair && bestPair.wr >= 55) {
    discoveries.push({
      type: 'edge',
      icon: '',
      title: `Your strongest edge: ${bestPair.pair}`,
      detail: `${bestPair.wr}% win rate across ${bestPair.count} trades${bestPair.avgRR > 0 ? ` · Avg ${bestPair.avgRR.toFixed(1)}R` : ''}`,
      insight: `You have a statistically meaningful edge on ${bestPair.pair}. Your data supports prioritising this instrument.`,
      color: '#00e5a0',
    });
  }

  if (worstPair && worstPair.wr <= 40 && worstPair.count >= 8 && worstPair.pair !== bestPair?.pair) {
    warnings.push({
      type: 'weakness',
      icon: '',
      title: `Avoid ${worstPair.pair} for now`,
      detail: `${worstPair.wr}% win rate across ${worstPair.count} trades · ${worstPair.pnl >= 0 ? '+' : ''}$${worstPair.pnl.toFixed(0)} total`,
      insight: `Your data shows no edge on ${worstPair.pair} yet. Consider pausing this instrument until you've reviewed what's going wrong.`,
      color: '#ff4d6d',
    });
  }

  // ── 2. SESSION PERFORMANCE ────────────────────────────────────
  const bySess = groupBy(closed.filter(t => t.session), 'session');
  const sessStats = Object.entries(bySess)
    .filter(([, ts]) => ts.length >= 5)
    .map(([session, ts]) => ({
      session,
      count: ts.length,
      wr:    Math.round((ts.filter(t => t.result === 'Win').length / ts.length) * 100),
      pnl:   ts.reduce((s, t) => s + Number(t.pnl || 0), 0),
    }))
    .sort((a, b) => b.wr - a.wr);

  if (sessStats.length >= 2) {
    const best  = sessStats[0];
    const worst = sessStats[sessStats.length - 1];
    if (best.wr - worst.wr >= 15) {
      discoveries.push({
        type: 'session',
        icon: '',
        title: `${best.session} is your best session`,
        detail: `${best.wr}% WR vs ${worst.wr}% in ${worst.session}`,
        insight: `You're significantly more profitable in ${best.session}. Consider focusing your trading time here and reducing ${worst.session} activity.`,
        color: '#4cc9f0',
      });
    }
  }

  // ── 3. OVERTRADING DETECTION ──────────────────────────────────
  const byDay = groupBy(closed, 'date');
  const dayTradeCounts = Object.values(byDay).map(ts => ts.length);
  const avgTradesPerDay = avg(dayTradeCounts);
  const highVolDays = Object.entries(byDay).filter(([, ts]) => ts.length > avgTradesPerDay * 1.8);

  if (highVolDays.length >= 3) {
    const highVolPnl = highVolDays.reduce((s, [, ts]) => s + ts.reduce((a, t) => a + Number(t.pnl || 0), 0), 0);
    const normalPnl  = Object.entries(byDay)
      .filter(([, ts]) => ts.length <= avgTradesPerDay * 1.2)
      .reduce((s, [, ts]) => s + ts.reduce((a, t) => a + Number(t.pnl || 0), 0), 0);
    const normalDays = Object.entries(byDay).filter(([, ts]) => ts.length <= avgTradesPerDay * 1.2).length;

    if (highVolDays.length >= 3 && highVolPnl < 0) {
      warnings.push({
        type: 'overtrading',
        icon: '',
        title: 'Overtrading is costing you money',
        detail: `High-volume days: ${highVolPnl >= 0 ? '+' : ''}$${highVolPnl.toFixed(0)} · Normal days: avg $${normalDays > 0 ? (normalPnl / normalDays).toFixed(0) : '0'}/day`,
        insight: `On days where you trade ${Math.round(avgTradesPerDay * 1.8)}+ times, you're losing money. Your data suggests stopping at ${Math.ceil(avgTradesPerDay)} trades per day.`,
        color: '#ffd166',
      });
    }
  }

  // ── 4. REVENGE TRADING COST ───────────────────────────────────
  const sortedByDate = [...closed].sort((a, b) => new Date(a.date + 'T' + (a.createdAt || '')) - new Date(b.date + 'T' + (b.createdAt || '')));
  let revengeWins = 0, revengeLosses = 0, revengePnl = 0;
  for (let i = 1; i < sortedByDate.length; i++) {
    const prev = sortedByDate[i - 1];
    const curr = sortedByDate[i];
    if (prev.result === 'Loss' && prev.date === curr.date) {
      // Trade taken same day right after a loss — potential revenge
      if (curr.result === 'Loss') revengeLosses++;
      else revengeWins++;
      revengePnl += Number(curr.pnl || 0);
    }
  }
  const revengeTrades = revengeWins + revengeLosses;
  if (revengeTrades >= 5) {
    const revengeWR = Math.round((revengeWins / revengeTrades) * 100);
    if (revengeWR < 45 || revengePnl < 0) {
      warnings.push({
        type: 'revenge',
        icon: '',
        title: 'Trades after same-day losses underperform',
        detail: `${revengeWR}% WR on re-entries after losses · ${revengePnl >= 0 ? '+' : ''}$${revengePnl.toFixed(0)} total`,
        insight: `Your win rate drops after same-day losses. Consider a rule: after a loss, take a 30-minute break before entering again.`,
        color: '#ff4d6d',
      });
    }
  }

  // ── 5. DISCIPLINE COST QUANTIFIED ────────────────────────────
  const ruleBreaks  = closed.filter(t => t.followedRules === false || t.followedRules === 'false');
  const ruleTrades  = closed.filter(t => t.followedRules === true  || t.followedRules === 'true');
  if (ruleBreaks.length >= 5 && ruleTrades.length >= 5) {
    const breakWR   = Math.round((ruleBreaks.filter(t => t.result === 'Win').length / ruleBreaks.length) * 100);
    const ruleWR    = Math.round((ruleTrades.filter(t => t.result === 'Win').length / ruleTrades.length) * 100);
    const breakPnl  = ruleBreaks.reduce((s, t) => s + Number(t.pnl || 0), 0);
    const rulePnl   = ruleTrades.reduce((s, t) => s + Number(t.pnl || 0), 0);
    if (ruleWR > breakWR + 10) {
      warnings.push({
        type: 'discipline',
        icon: '',
        title: `Rule breaks cost you $${Math.abs(breakPnl).toFixed(0)}`,
        detail: `Following rules: ${ruleWR}% WR · Rule breaks: ${breakWR}% WR`,
        insight: `Your win rate is ${ruleWR - breakWR}% higher when you follow your rules. Every rule break trades your edge away. Your ${ruleBreaks.length} rule breaks cost $${Math.abs(breakPnl).toFixed(0)} vs the rule-following P&L of +$${rulePnl.toFixed(0)}.`,
        color: '#ffd166',
      });
    }
  }

  // ── 6. WIN/LOSS QUALITY ───────────────────────────────────────
  const avgWin  = avg(closed.filter(t => t.result === 'Win').map(t => Number(t.pnl || 0)));
  const avgLoss = Math.abs(avg(closed.filter(t => t.result === 'Loss').map(t => Number(t.pnl || 0))));
  if (avgWin > 0 && avgLoss > 0) {
    const ratio = avgWin / avgLoss;
    if (ratio < 1) {
      warnings.push({
        type: 'winloss',
        icon: '',
        title: 'Your average loss exceeds your average win',
        detail: `Avg win: $${avgWin.toFixed(0)} · Avg loss: $${avgLoss.toFixed(0)} · Ratio: ${ratio.toFixed(2)}`,
        insight: `You're losing more per loss than you're winning per win. You need a win rate above ${Math.round((avgLoss / (avgWin + avgLoss)) * 100)}% just to break even. Focus on either cutting losses shorter or letting winners run longer.`,
        color: '#ff9f43',
      });
    } else if (ratio >= 2) {
      discoveries.push({
        type: 'quality',
        icon: '',
        title: 'Excellent win/loss ratio',
        detail: `Avg win: $${avgWin.toFixed(0)} · Avg loss: $${avgLoss.toFixed(0)} · Ratio: ${ratio.toFixed(2)}x`,
        insight: `Your winners are ${ratio.toFixed(1)}x larger than your losers. This is strong execution — you're letting winners run and cutting losses short.`,
        color: '#00e5a0',
      });
    }
  }

  // ── 7. CONSECUTIVE LOSS RESPONSE ─────────────────────────────
  let streak = 0, maxStreak = 0;
  const streakResponses = [];
  for (let i = 0; i < sortedByDate.length; i++) {
    if (sortedByDate[i].result === 'Loss') {
      streak++;
      maxStreak = Math.max(maxStreak, streak);
    } else {
      if (streak >= 2 && i < sortedByDate.length - 1) {
        streakResponses.push({ streak, nextResult: sortedByDate[i].result });
      }
      streak = 0;
    }
  }

  // ── 8. TRADE COUNT SWEET SPOT ────────────────────────────────
  if (Object.keys(byDay).length >= 10) {
    const dayPerf = Object.entries(byDay).map(([, ts]) => ({
      count: ts.length,
      wr:    Math.round((ts.filter(t => t.result === 'Win').length / ts.length) * 100),
      pnl:   ts.reduce((s, t) => s + Number(t.pnl || 0), 0),
    }));

    // Group by trade count
    const byCount = {};
    for (const d of dayPerf) {
      if (!byCount[d.count]) byCount[d.count] = [];
      byCount[d.count].push(d);
    }

    const countStats = Object.entries(byCount)
      .filter(([, days]) => days.length >= 3)
      .map(([count, days]) => ({
        count: Number(count),
        avgWR:  Math.round(avg(days.map(d => d.wr))),
        avgPnl: avg(days.map(d => d.pnl)),
        days:   days.length,
      }))
      .sort((a, b) => b.avgWR - a.avgWR);

    const bestCount = countStats[0];
    if (bestCount && countStats.length >= 2) {
      discoveries.push({
        type: 'tradecount',
        icon: '',
        title: `Your sweet spot: ${bestCount.count} trade${bestCount.count > 1 ? 's' : ''} per day`,
        detail: `${bestCount.avgWR}% WR on days with exactly ${bestCount.count} trade${bestCount.count > 1 ? 's' : ''} (${bestCount.days} days)`,
        insight: `Your data shows your best performance comes on days with ${bestCount.count} trade${bestCount.count > 1 ? 's' : ''}. Quality over quantity.`,
        color: '#00e5a0',
      });
    }
  }

  // Combine and sort — warnings first, then discoveries
  const allInsights = [
    ...warnings.map(w => ({ ...w, category: 'warning' })),
    ...discoveries.map(d => ({ ...d, category: 'discovery' })),
  ];

  return {
    insights: allInsights,
    hasData: closed.length >= 10,
    tradeCount: closed.length,
    pairStats,
    sessStats,
  };
}

// ── HELPERS ───────────────────────────────────────────────────────
function groupBy(arr, key) {
  return arr.reduce((g, item) => {
    const k = item[key] || 'Unknown';
    if (!g[k]) g[k] = [];
    g[k].push(item);
    return g;
  }, {});
}

function avg(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}
